import { debug } from './debug';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Get current date/time in merchant's timezone
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {Date} Current date/time in the merchant's timezone, or browser local time if no timezone provided
 *
 * NOTE: This function is for display and opening hours logic only.
 * For booking timestamps, use getCurrentUTCDate() instead.
 */
export function getCurrentDateInTimezone(timezone = null) {
  return timezone ? toZonedTime(new Date(), timezone) : new Date();
}

/**
 * Determine whether the booking-cutoff deadline for a rental has already passed.
 *
 * Mirrors the deadline math used by DateSelection.isDateDisabled and the
 * check_availability_enhanced RPC: the deadline is
 *   (rental start date − cutoff_days_prior days) at cutoff_time,
 * evaluated in the merchant's local timezone. cutoff_time is a bare wall-clock
 * time-of-day in the merchant's timezone, so both "now" and the deadline are
 * compared in merchant-local terms.
 *
 * @param {string|number|Date} startDate - Rental start instant (UTC ISO string, etc.)
 * @param {number|null} cutoffDaysPrior - Days before the rental for the cutoff (0 = same day)
 * @param {string|null} cutoffTime - Time-of-day "HH:MM[:SS]" in merchant timezone
 * @param {string|null} timezone - IANA merchant timezone (falls back to browser local)
 * @returns {boolean} true if the cutoff deadline is in the past (rental should be blocked)
 */
export function isAfterCutoff(startDate, cutoffDaysPrior, cutoffTime, timezone = null) {
  // No cutoff configured (both parts required) → nothing to enforce.
  if (cutoffDaysPrior === null || cutoffDaysPrior === undefined || !cutoffTime) {
    return false;
  }

  const start = new Date(startDate);
  if (isNaN(start.getTime())) {
    return false; // Can't evaluate without a valid start; don't over-filter.
  }

  // "now" and the rental start both expressed in merchant-local wall-clock terms.
  const now = getCurrentDateInTimezone(timezone);
  const startLocal = timezone ? toZonedTime(start, timezone) : start;

  // deadline = (rental date − cutoff_days_prior) at cutoff_time, merchant-local.
  const deadline = new Date(startLocal);
  deadline.setDate(deadline.getDate() - cutoffDaysPrior);
  const [hours, minutes] = cutoffTime.split(':').map(Number);
  deadline.setHours(hours, minutes || 0, 0, 0);

  return now > deadline;
}

/**
 * Get current UTC date/time for booking timestamps
 * @returns {Date} Current UTC time (no timezone conversion)
 *
 * Use this for immediate booking start times to avoid timezone double-conversion bugs.
 * The database stores all timestamps in UTC, so we send UTC directly.
 */
export function getCurrentUTCDate() {
  return new Date();
}

/**
 * Parse time string like "08:00" to hours (8.0) or "08:30" to 8.5
 */
export function parseTimeToHours(timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours + minutes / 60;
}

/**
 * Latest opening-hours close (wall-clock hours) for the weekday of `date`.
 *
 * Multi-window days resolve to the last window's close. Returns null when the
 * store has no opening hours that weekday. `date` supplies only the weekday, so
 * it must already be in merchant-local terms (as calendar dates in the booking
 * flows are); no timezone conversion is applied.
 *
 * @param {Object} openingHours - Opening hours object keyed by lowercase day name
 * @param {Date} date - A date whose weekday selects the opening-hours window
 * @returns {number|null} Close time in wall-clock hours, or null if closed
 */
export function getOpeningCloseHourForDate(openingHours, date) {
  if (!openingHours || !date || isNaN(date.getTime())) return null;
  const dayName = DAY_NAMES[date.getDay()];
  const windows = normalizeWindows(openingHours[dayName]);
  if (windows.length === 0) return null;
  return windows.reduce((latest, w) => {
    const h = parseTimeToHours(w.close);
    return h > latest ? h : latest;
  }, -Infinity);
}

/**
 * Get current time in hours (e.g., 14.5 for 2:30 PM)
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 */
export function getCurrentTimeInHours(timezone = null) {
  // Get current time in the specified timezone for opening hours logic
  const now = timezone ? toZonedTime(new Date(), timezone) : new Date();
  return now.getHours() + now.getMinutes() / 60;
}

/**
 * Check if store is currently open
 * @param {Object} openingHours - Opening hours object with day keys
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {Object} { isOpen: boolean, message: string, currentHours: object }
 */
export function isStoreCurrentlyOpen(openingHours, timezone = null) {
  if (!openingHours) {
    return {
      isOpen: true, // Assume open if no hours specified
      message: null,
      currentHours: null
    };
  }

  // Get current time in the merchant's timezone
  const now = getCurrentDateInTimezone(timezone);
  const dayName = DAY_NAMES[now.getDay()];
  const todayHours = openingHours[dayName];

  if (!todayHours) {
    return {
      isOpen: false,
      message: 'Store is closed today.',
      messageKey: 'time.storeClosedToday',
      messageParams: {},
      currentHours: null
    };
  }

  const currentTime = getCurrentTimeInHours(timezone);
  const openTime = parseTimeToHours(todayHours.open);
  const closeTime = parseTimeToHours(todayHours.close);

  if (currentTime < openTime) {
    return {
      isOpen: false,
      message: `Store opens at ${todayHours.open}. Please use "Make Reservation" to book for later today or future dates.`,
      messageKey: 'time.storeOpensAt',
      messageParams: { open: todayHours.open },
      currentHours: todayHours,
      openTime,
      closeTime
    };
  }

  if (currentTime >= closeTime) {
    return {
      isOpen: false,
      message: `Store is closed (closed at ${todayHours.close}). Please use "Make Reservation" to book for tomorrow or future dates.`,
      messageKey: 'time.storeClosedAt',
      messageParams: { close: todayHours.close },
      currentHours: todayHours,
      openTime,
      closeTime
    };
  }

  return {
    isOpen: true,
    message: null,
    currentHours: todayHours,
    openTime,
    closeTime,
    remainingHours: closeTime - currentTime
  };
}

/**
 * Get remaining open hours from now until closing
 * @param {Object} openingHours - Opening hours object
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {number} Hours remaining until close (or null if closed)
 */
export function getRemainingOpenHours(openingHours, timezone = null) {
  const status = isStoreCurrentlyOpen(openingHours, timezone);
  return status.isOpen ? status.remainingHours : null;
}

/**
 * Parse duration string (e.g., "4h", "1d", "2w") to hours
 */
export function parseDurationToHours(duration) {
  if (!duration) return 4; // Default 4 hours

  // Handle if duration is an object with id property
  const durationStr = typeof duration === 'string' ? duration : duration?.id || duration?.value || '';

  if (typeof durationStr !== 'string' || durationStr === '') {
    debug.warn('Invalid duration format:', duration);
    return 4;
  }

  if (durationStr.endsWith('h')) {
    return parseInt(durationStr);
  } else if (durationStr.endsWith('d')) {
    return parseInt(durationStr) * 24;
  } else if (durationStr.endsWith('w')) {
    return parseInt(durationStr) * 24 * 7;
  }
  return 4;
}

/**
 * Check if a duration is valid for "Start Now" given current opening hours
 * @param {string} durationId - Duration ID like "4h", "1d"
 * @param {Object} openingHours - Opening hours object
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @param {Array<{start_date: string, end_date: string}>} [closingPeriods] - Closing periods to exclude
 * @param {string} [durationType] - The rule's duration_type ('calendar_day'
 *   returns at the same time N days later; anything else ends at closing)
 * @returns {boolean} True if duration is valid
 */
export function isDurationValidForStartNow(durationId, openingHours, timezone = null, closingPeriods = [], durationType = null) {
  const status = isStoreCurrentlyOpen(openingHours, timezone);

  // Must be open right now to begin an immediate rental.
  if (!status.isOpen) {
    return false;
  }

  // Validate the ACTUAL return moment against opening hours and closing
  // periods, exactly as the server does (api/create-booking.js,
  // isMomentWithinOpeningHours) — for every duration unit:
  //   - Hourly: now + N hours, rather than requiring it to fit before *today's*
  //     close. A 12h rental started at 15:40 at a 00:00–23:59 location returns
  //     at 03:40, inside the next day's window; the same rental at a
  //     09:00–18:00 store is hidden.
  //   - Day/week: closing time on the return day (or, for calendar_day, the
  //     same time N days later), as Checkout computes it. Hidden when the return
  //     day is closed or inside a closing period — e.g. "2 days" started on a
  //     Saturday at a store closed on Sundays, which the server would refuse at
  //     checkout.
  // Uses the same start instant the booking will use (getCurrentUTCDate, the
  // value BookingFlow assigns for immediate startDate).
  const returnMoment = computeReturnDateTime(
    getCurrentUTCDate(),
    durationId,
    openingHours,
    durationType,
    timezone
  );
  if (!returnMoment) {
    return false;
  }
  return isMomentWithinOpeningHours(returnMoment, openingHours, closingPeriods, timezone).valid;
}

/**
 * Normalize a day's opening hours into an array of windows.
 * Supports:
 *   - null/undefined                         → []
 *   - { open: "HH:mm", close: "HH:mm" }      → [{open, close}]
 *   - [{ open, close }, { open, close }]     → same array, filtered for valid entries
 */
function normalizeWindows(dayHours) {
  if (!dayHours) return [];
  if (Array.isArray(dayHours)) {
    return dayHours.filter((w) => w && w.open && w.close);
  }
  if (dayHours.open && dayHours.close) return [dayHours];
  return [];
}

/**
 * Compute the rental return moment for a given start moment and duration.
 *
 * Mirrors the logic used in Checkout / StripePaymentElement:
 *   - Hourly rentals ("4h", "24h"):   start + N hours
 *   - Day rentals   ("1d", "2d"):     closing time on (start + days - 1)
 *   - Week rentals  ("1w", "2w"):     closing time on (start + weeks*7 - 1)
 *
 * For day/week rentals, returns null if the computed return day has no
 * opening hours — callers should treat that as an invalid return moment.
 *
 * Shared with api/_utils/openingHours.js — keep in sync.
 *
 * @param {Date|string} startDateTime
 * @param {string} durationId - e.g. "4h", "1d", "2d", "1w"
 * @param {Object} openingHours - Opening hours object keyed by lowercase day name
 * @param {string} [durationType] - Original pricing rule duration_type.
 *   When 'calendar_day', day rentals use rolling N×24h instead of closing
 *   time on (start + N-1). Defaults to null (multi_day convention).
 * @param {string} [timezone] - IANA merchant timezone. When provided, the
 *   return-day calendar math for multi_day/week rentals is performed in the
 *   merchant's local timezone (so the return day's weekday + closing time
 *   line up with the merchant's opening_hours definitions regardless of the
 *   browser's timezone). When omitted, falls back to browser-local math
 *   (legacy behaviour — only correct when browser tz == merchant tz).
 * @returns {Date|null}
 */
export function computeReturnDateTime(startDateTime, durationId, openingHours, durationType = null, timezone = null) {
  if (!startDateTime || !durationId) return null;

  const start = new Date(startDateTime);
  if (isNaN(start.getTime())) return null;

  // Hourly rentals: simple addition
  if (durationId.endsWith('h')) {
    const hours = parseInt(durationId, 10);
    if (isNaN(hours)) return null;
    const returnDate = new Date(start);
    returnDate.setHours(returnDate.getHours() + hours);
    return returnDate;
  }

  // Day/week rentals
  let days = 0;
  if (durationId.endsWith('d')) days = parseInt(durationId, 10);
  else if (durationId.endsWith('w')) days = parseInt(durationId, 10) * 7;
  else return null;

  if (isNaN(days) || days <= 0) return null;

  // calendar_day: rolling N × 24h from start (same time-of-day, N days later)
  if (durationType === 'calendar_day') {
    const returnDate = new Date(start);
    returnDate.setDate(returnDate.getDate() + days);
    return returnDate;
  }

  // multi_day / week (default): ends at closing time on the return day.
  // Resolve the return day + closing time in merchant local time when a
  // timezone is provided so DST transitions and cross-tz users are handled
  // correctly.
  //
  // Roll-forward rule: if the natural return-day close lands at or before the
  // start instant — which happens when pickup_hours let a (single-)day rental
  // start at/after the opening close, so a same-day return would be <= start —
  // advance to the next OPENING day whose close is strictly after the start.
  // This guarantees end > start (no zero/negative window) while honouring a late
  // pickup with a next-day return. Bounded to avoid an unbounded loop. A closed
  // *natural* return day still yields null (original contract); days reached
  // while rolling are skipped.
  const MAX_RETURN_ROLL_DAYS = 14;
  if (timezone) {
    const localReturn = toZonedTime(start, timezone);
    localReturn.setDate(localReturn.getDate() + (days - 1));
    for (let i = 0; i <= MAX_RETURN_ROLL_DAYS; i++) {
      const dayName = DAY_NAMES[localReturn.getDay()];
      const windows = normalizeWindows(openingHours?.[dayName]);
      if (i === 0 && windows.length === 0) return null;
      if (windows.length > 0) {
        const lastClose = windows.reduce((latest, w) => {
          const h = parseTimeToHours(w.close);
          return h > latest ? h : latest;
        }, -Infinity);
        const closeHour = Math.floor(lastClose);
        const closeMinute = Math.round((lastClose - closeHour) * 60);
        const y = localReturn.getFullYear();
        const mo = String(localReturn.getMonth() + 1).padStart(2, '0');
        const d = String(localReturn.getDate()).padStart(2, '0');
        const hh = String(closeHour).padStart(2, '0');
        const mm = String(closeMinute).padStart(2, '0');
        const candidate = fromZonedTime(`${y}-${mo}-${d}T${hh}:${mm}:00`, timezone);
        if (candidate.getTime() > start.getTime()) return candidate;
      }
      localReturn.setDate(localReturn.getDate() + 1);
    }
    return null;
  }

  // Legacy browser-local fallback (preserved for callers that don't yet
  // thread the merchant timezone through — behaviour matches what shipped
  // before the tz-awareness refactor).
  const returnDate = new Date(start);
  returnDate.setDate(returnDate.getDate() + (days - 1));
  for (let i = 0; i <= MAX_RETURN_ROLL_DAYS; i++) {
    const dayName = DAY_NAMES[returnDate.getDay()];
    const windows = normalizeWindows(openingHours?.[dayName]);
    if (i === 0 && windows.length === 0) return null;
    if (windows.length > 0) {
      const lastClose = windows.reduce((latest, w) => {
        const h = parseTimeToHours(w.close);
        return h > latest ? h : latest;
      }, -Infinity);
      const closeHour = Math.floor(lastClose);
      const closeMinute = Math.round((lastClose - closeHour) * 60);
      const candidate = new Date(returnDate);
      candidate.setHours(closeHour, closeMinute, 0, 0);
      if (candidate.getTime() > start.getTime()) return candidate;
    }
    returnDate.setDate(returnDate.getDate() + 1);
  }
  return null;
}

/**
 * Validate that a specific moment falls within the location's opening hours
 * and is not inside a closing period.
 *
 * This is the shared return-moment validator. Rules:
 *   1. The date (in merchant local time) must not fall within any closing period.
 *   2. The store must have opening hours on that weekday.
 *   3. The time component must fall inside one of that day's opening windows
 *      (open inclusive, close inclusive).
 *
 * Callers supply the merchant timezone when the input Date is in UTC (e.g.
 * server-side where incoming timestamps are UTC). On the client, calendar
 * dates built from local year/month/day can be passed with timezone=null.
 *
 * Shared with api/_utils/openingHours.js — keep in sync.
 *
 * @param {Date|string} moment
 * @param {Object} openingHours - Opening hours object keyed by lowercase day name
 * @param {Array<{start_date: string, end_date: string}>} closingPeriods
 * @param {string|null} [timezone] - IANA timezone (e.g. "Europe/Berlin")
 * @returns {{ valid: boolean, reason: string|null }}
 */
export function isMomentWithinOpeningHours(
  moment,
  openingHours,
  closingPeriods = [],
  timezone = null
) {
  if (!moment) return { valid: false, reason: 'No moment provided' };

  const date = typeof moment === 'string' ? new Date(moment) : new Date(moment);
  if (isNaN(date.getTime())) {
    return { valid: false, reason: 'Invalid date' };
  }

  // Evaluate the moment in merchant local time so weekday + time match
  // the opening_hours definitions (which are always in merchant local time).
  const localDate = timezone ? toZonedTime(date, timezone) : date;

  // 1. Closing period check — date-level comparison in merchant local YYYY-MM-DD
  const yyyy = localDate.getFullYear();
  const mm = String(localDate.getMonth() + 1).padStart(2, '0');
  const dd = String(localDate.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd}`;

  const inClosingPeriod = (closingPeriods || []).some((period) => {
    const start = period?.start_date;
    const end = period?.end_date;
    return start && end && dateStr >= start && dateStr <= end;
  });

  if (inClosingPeriod) {
    return {
      valid: false,
      reason: `Date ${dateStr} falls within a closing period`,
    };
  }

  // 2. Weekday must have opening hours
  const dayName = DAY_NAMES[localDate.getDay()];
  const windows = normalizeWindows(openingHours?.[dayName]);

  if (windows.length === 0) {
    return {
      valid: false,
      reason: `Store is closed on ${dayName}`,
    };
  }

  // 3. Time component must fall inside one of the day's opening windows
  const timeHours = localDate.getHours() + localDate.getMinutes() / 60;
  const withinWindow = windows.some((w) => {
    const openHour = parseTimeToHours(w.open);
    const closeHour = parseTimeToHours(w.close);
    return timeHours >= openHour && timeHours <= closeHour;
  });

  if (!withinWindow) {
    const hh = String(localDate.getHours()).padStart(2, '0');
    const mn = String(localDate.getMinutes()).padStart(2, '0');
    const windowsStr = windows.map((w) => `${w.open}-${w.close}`).join(', ');
    return {
      valid: false,
      reason: `Time ${hh}:${mn} is outside opening hours on ${dayName} (${windowsStr})`,
    };
  }

  return { valid: true, reason: null };
}

/**
 * Decide whether a single pickup/delivery ("handoff") start slot should be
 * offered. This is the single source of truth shared by the pickup and delivery
 * paths of the reservation time picker — extract-and-reuse rather than two
 * parallel copies.
 *
 * Behavior matrix (restrictHandoffs = the location's
 * restrict_handoffs_to_opening_hours toggle):
 *
 *                     | toggle false (default)      | toggle true
 *   ------------------+-----------------------------+---------------------------
 *   pickup, hourly    | return validated (always)   | return validated
 *   pickup, multi_day | may roll to next day        | must start before close
 *   delivery, hourly  | no validation (lenient)     | return validated
 *   delivery, multi_day| may roll to next day       | must start before close
 *
 * Rationale for the asymmetry: pickup hourly returns have ALWAYS been validated
 * against opening hours (offering a pickup whose return lands after close is a
 * bug), so that stays on regardless of the toggle. Deliveries historically
 * skipped all opening-hours validation; the toggle brings them up to pickup
 * parity. The multi_day close-gate keys on durationType === 'multi_day' (so a
 * multi_day rule presented as '1w' is covered; calendar_day / true week rules
 * are not gated) and prevents the next-day-return roll-forward in
 * computeReturnDateTime.
 *
 * @param {Object} params
 * @param {boolean} params.isDelivery - Whether this is a delivery location
 * @param {boolean} params.restrictHandoffs - The location toggle value
 * @param {string} params.durationId - e.g. '4h', '1d', '1w'
 * @param {string} params.durationType - pricing rule duration_type ('hours',
 *   'multi_day', 'calendar_day', 'week', ...)
 * @param {number} params.rentalDurationHours - Duration in hours (fallback
 *   classifier when durationId is missing)
 * @param {number} params.hour - Candidate handoff time (wall-clock hours)
 * @param {Date} params.slotStartUtc - The candidate start as a UTC instant
 *   (merchant-local wall time → UTC); used to compute the hourly return moment
 * @param {Object} params.openingHours - Opening hours map
 * @param {Array} [params.closingPeriods] - Closing periods
 * @param {string|null} [params.timezone] - IANA merchant timezone
 * @param {number} params.openingCloseHour - That day's opening close (wall hours)
 * @param {number} params.storeCloseHour - Fallback close for the degenerate
 *   case where a precise return moment can't be computed
 * @returns {boolean} true if the slot should be offered
 */
export function isHandoffSlotAllowed({
  isDelivery,
  restrictHandoffs,
  durationId,
  durationType,
  rentalDurationHours,
  hour,
  slotStartUtc,
  openingHours,
  closingPeriods = [],
  timezone = null,
  openingCloseHour,
  storeCloseHour,
}) {
  // Delivery with the toggle off keeps its legacy lenient behavior: offer every
  // slot in the window without opening-hours validation. Pickups always apply
  // the rules below (hourly validation is not toggle-gated).
  const applyRules = !isDelivery || restrictHandoffs;
  if (!applyRules) return true;

  // Classify by duration UNIT, not magnitude: an hourly rule returns at
  // start + N hours regardless of size, so a 24h+ hourly rate must still be
  // validated as hourly rather than routed into the day/week path.
  const isDayOrWeekRental = durationId
    ? (durationId.endsWith('d') || durationId.endsWith('w'))
    : (rentalDurationHours >= 24);

  if (isDayOrWeekRental) {
    // multi_day close-gate: no handoff at/after that day's opening close.
    if (restrictHandoffs && durationType === 'multi_day' && hour >= openingCloseHour) {
      return false;
    }
    return true;
  }

  // Hourly: the actual return moment (start + N hours) must fall within opening
  // hours, so rentals that cross midnight into the next open day are allowed
  // while returns landing outside opening hours are excluded.
  const returnMoment = computeReturnDateTime(slotStartUtc, durationId, openingHours, durationType, timezone);
  return returnMoment
    ? isMomentWithinOpeningHours(returnMoment, openingHours, closingPeriods, timezone).valid
    // Fallback when a precise return moment can't be computed (e.g. the duration
    // id is missing/unparseable): preserve the legacy same-day check so we never
    // over-filter in a degenerate case.
    : (hour + rentalDurationHours <= storeCloseHour);
}
